import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Tooltip as MuiTooltip,
  FormHelperText,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Sync as SyncIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import NotificationSnackbar, { NotificationType } from '../../../components/common/NotificationSnackBar';
import {
  createPronunciationRuleEntry,
  deletePronunciationRuleEntry,
  syncPronunciationRules,
  updatePronunciationRuleEntry,
  listPronunciationRuleEntries,
} from '../api';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import { getWebsitesByPublisher, generateVoiceSample } from '../../../api/apiClient';

interface PronunciationRulesTabProps {
  publisherId: string;
  onChanged?: () => void; // ask parent to refresh publisher (optional)
}

// Helper to extract common fields from an entry (best-effort)
const getEntryFields = (entry: any) => {
  return {
    id: entry?.id ?? entry?.entryId ?? null,
    // Server uses `match`; legacy uses `search` / `phrase` / `text`
    phrase: entry?.match ?? entry?.search ?? entry?.phrase ?? entry?.text ?? '',
    // Server uses `displayAs`; legacy uses `replace` / `pronunciation`
    pronunciation: entry?.displayAs ?? entry?.replace ?? entry?.pronunciation ?? '',
    // Server uses `kind`; legacy uses `matchType`
    matchType: entry?.kind ?? entry?.matchType ?? 'exact',
    caseSensitive: !!entry?.caseSensitive,
    enabled: entry?.enabled !== false, // default true
    language: entry?.language ?? entry?.lang ?? '',
    priority: typeof entry?.priority === 'number' ? entry?.priority : undefined,
    tags: Array.isArray(entry?.tags) ? entry?.tags : undefined,
    appliesToVoices: Array.isArray(entry?.appliesToVoices) ? entry?.appliesToVoices : undefined,
  };
};

const PronunciationRulesTab: React.FC<PronunciationRulesTabProps> = ({ publisherId, onChanged }) => {
  const [localEntries, setLocalEntries] = useState<any[] | null | undefined>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' as NotificationType });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [phrase, setPhrase] = useState<string>('');
  const [pronunciation, setPronunciation] = useState<string>('');
  const [matchType, setMatchType] = useState<'exact' | 'word' | 'regex' | 'phrase'>('exact');
  const [caseSensitive, setCaseSensitive] = useState<boolean>(false);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [language, setLanguage] = useState<string>('');
  const [priority, setPriority] = useState<number | ''>('');
  const [tags, setTags] = useState<string>(''); // comma-separated
  const [appliesToVoices, setAppliesToVoices] = useState<string>(''); // comma-separated voice IDs
  const [regexError, setRegexError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ phrase?: string; pronunciation?: string } | null>(null);
  const [alphabet, setAlphabet] = useState<'spelling' | 'ipa' | 'arpabet'>('spelling');
  const [notes, setNotes] = useState<string>('');
  const [showPronunciationTips, setShowPronunciationTips] = useState<boolean>(false);

  // Audio test state
  const [testWebsiteId, setTestWebsiteId] = useState<string>('');
  // Removed unused publisherWebsites state
  const [testVoiceId, setTestVoiceId] = useState<string>('');
  const [testText, setTestText] = useState<string>('');
  const [isTestingSpeech, setIsTestingSpeech] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const DEFAULT_TEST_VOICE = '1SM7GgM6IMuvQlz2BwM3';
  const DEFAULT_TEST_TEXT = `[calm] Good morning, Sonoma Valley, [cheerfully] you're tuned to Location Station, [pause] your clear and kind briefing; [curious] what's ahead today matters, [emphasized] to you, [understated] and we'll keep it measured, [whispers] just between us, the coffee festival is back, [laughs] finally, [drawn out] so... we're ready, [rapid-fire] headlines, weather, traffic, community.\n\n[rushed] Quick safety note until 8 p.m., [slows down] avoid open flames near the ridge trail, keep vehicles off dry grass, [pause] CalFire tip line at 707-555-0134; [deadpan] that's the official guidance, [flatly] no changes since this morning, [stammers] we... we'll update if it shifts, [emphasized] immediately.\n\n[somber] One serious item we're following, [understated] a budget shortfall affecting library hours, [pause] details at the meeting tonight; [sigh] we know that's tough for families, [slows down] especially after-school, [calm] we'll break it down later, [cheerfully] and we still have good news on park funding to share.`;

  // LocalStorage keys for test controls
  const LS_KEY_TEST_VOICE = 'ls.pronunciationTest.voiceId';
  const LS_KEY_TEST_TEXT = 'ls.pronunciationTest.text';

  const [hydrated, setHydrated] = useState<boolean>(false);

  // Load websites for this publisher (needed to call website voice-sample endpoint)
  useEffect(() => {
    (async () => {
      try {
        const sites = await getWebsitesByPublisher(publisherId);
        if (Array.isArray(sites) && sites.length && !testWebsiteId) {
          const firstId = (sites[0]?.id || sites[0]?.PK || '').toString();
          setTestWebsiteId(firstId);
        }
      } catch (e) {
        console.error('Failed to load websites for audio test', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  // Prefill from localStorage (or defaults if empty) on first load
  useEffect(() => {
    try {
      const v = localStorage.getItem(LS_KEY_TEST_VOICE) || '';
      const t = localStorage.getItem(LS_KEY_TEST_TEXT) || '';
      if (v) setTestVoiceId(v);
      if (t) setTestText(t);
      if (!v && !t) {
        setTestVoiceId(DEFAULT_TEST_VOICE);
        setTestText(DEFAULT_TEST_TEXT);
      }
    } finally {
      // Mark hydrated so that subsequent effects can persist safely
      setHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to localStorage when user changes values (after hydration only)
  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(LS_KEY_TEST_VOICE, testVoiceId || ''); } catch {}
  }, [testVoiceId, hydrated]);
  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(LS_KEY_TEST_TEXT, testText || ''); } catch {}
  }, [testText, hydrated]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const handleUseDefaults = () => {
    setTestText(DEFAULT_TEST_TEXT);
    setTestVoiceId(DEFAULT_TEST_VOICE);
  };

  const handleTestSpeech = async () => {
    if (!testWebsiteId) {
      setNotification({ open: true, message: 'No website available. Add a website to this publisher to test speech.', type: 'error' });
      return;
    }
    if (!testText.trim()) {
      setNotification({ open: true, message: 'Enter some text to convert to speech.', type: 'error' });
      return;
    }
    if (!testVoiceId.trim()) {
      setNotification({ open: true, message: 'Enter a voiceId or click Use Defaults.', type: 'error' });
      return;
    }
    setIsTestingSpeech(true);
    try {
      const res = await generateVoiceSample(testWebsiteId, testVoiceId.trim(), testText);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      const url = URL.createObjectURL(res.audio);
      setAudioUrl(url);
      showSuccess('Voice sample ready');
    } catch (e: any) {
      console.error('Test speech failed:', e);
      showError(e?.response?.data?.error || e?.message || 'Failed to generate voice sample');
    } finally {
      setIsTestingSpeech(false);
    }
  };

  const loadEntries = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const entries = await listPronunciationRuleEntries(publisherId);
      setLocalEntries(entries);
    } catch (e: any) {
      console.error('Failed to load pronunciation rules:', e);
      setLocalEntries([]);
      setNotification({ open: true, message: e?.response?.data?.error || e?.message || 'Failed to load rules', type: 'error' });
    } finally {
      setIsLoadingList(false);
    }
  }, [publisherId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const hasEntries = useMemo(() => Array.isArray(localEntries) && localEntries.length > 0, [localEntries]);

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingEntryId(null);
    setPhrase('');
    setPronunciation('');
    setMatchType('exact');
    setCaseSensitive(false);
    setEnabled(true);
    setLanguage('');
    setPriority('');
    setTags('');
    setAppliesToVoices('');
    setRegexError(null);
    setFieldErrors(null);
    setAlphabet('spelling');
    setNotes('');
    setShowPronunciationTips(false);
    setDialogOpen(true);
  };

  const openEditDialog = (entry: any) => {
    const f = getEntryFields(entry);
    setDialogMode('edit');
    setEditingEntryId(f.id);
    setPhrase(f.phrase || '');
    setPronunciation(f.pronunciation || '');
    setMatchType((['exact','word','regex','phrase'] as const).includes(f.matchType) ? f.matchType : 'exact');
    setCaseSensitive(!!f.caseSensitive);
    setEnabled(f.enabled !== false);
    setLanguage(f.language || '');
    setPriority(typeof f.priority === 'number' ? f.priority : '');
    setTags(Array.isArray(f.tags) ? f.tags.join(', ') : '');
    setAppliesToVoices(Array.isArray(f.appliesToVoices) ? f.appliesToVoices.join(', ') : '');
    setAlphabet((entry?.alphabet === 'ipa' || entry?.alphabet === 'arpabet') ? entry.alphabet : 'spelling');
    setNotes(typeof entry?.notes === 'string' ? entry.notes : '');
    setRegexError(null);
    setFieldErrors(null);
    setShowPronunciationTips(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const validateRegexIfNeeded = (mt: string, phr: string) => {
    if (mt === 'regex') {
      try {
        // eslint-disable-next-line no-new
        new RegExp(phr);
        setRegexError(null);
      } catch (e: any) {
        setRegexError(e?.message || 'Invalid regular expression');
      }
    } else {
      setRegexError(null);
    }
  };

  const showError = (message: string) => setNotification({ open: true, message, type: 'error' });
  const showSuccess = (message: string) => setNotification({ open: true, message, type: 'success' });

  const handleSave = async () => {
    const errs: { phrase?: string; pronunciation?: string } = {};
    if (!phrase.trim()) errs.phrase = 'Required';
    if (!pronunciation.trim()) errs.pronunciation = 'Required';
    setFieldErrors(Object.keys(errs).length ? errs : null);
    if (Object.keys(errs).length) return;

    validateRegexIfNeeded(matchType, phrase);
    if (regexError) return;

    const payload: any = {
      // Prefer server field names, include legacy for backward compatibility
      match: phrase.trim(),
      displayAs: pronunciation.trim(),
      kind: matchType,
      search: phrase.trim(),
      replace: pronunciation.trim(),
      matchType,
      caseSensitive: !!caseSensitive,
      enabled: !!enabled,
    };
    if (alphabet && alphabet !== 'spelling') payload.alphabet = alphabet; // 'ipa' | 'arpabet'
    if (language.trim()) payload.language = language.trim();
    if (priority !== '' && priority !== null) payload.priority = Number(priority);
    const parsedTags = tags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (parsedTags.length) payload.tags = parsedTags;
    const parsedVoices = appliesToVoices
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (parsedVoices.length) payload.appliesToVoices = parsedVoices;
    if (notes.trim()) payload.notes = notes.trim();

    setIsBusy(true);
    try {
      if (dialogMode === 'create') {
        await createPronunciationRuleEntry(publisherId, payload);
        showSuccess('Rule created');
      } else if (dialogMode === 'edit') {
        if (!editingEntryId) {
          showError('Cannot update: entry id is missing');
          setIsBusy(false);
          return;
        }
        await updatePronunciationRuleEntry(publisherId, editingEntryId, payload);
        showSuccess('Rule updated');
      }
      setDialogOpen(false);
      await loadEntries();
      onChanged?.();
    } catch (e: any) {
      console.error('Save pronunciation rule failed:', e);
      showError(e?.response?.data?.error || e?.message || 'Failed to save rule');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (entry: any) => {
    const id = entry?.id ?? entry?.entryId;
    if (!id) {
      showError('Cannot delete: entry id is missing');
      return;
    }
    if (!window.confirm('Delete this rule? This cannot be undone.')) return;
    setIsBusy(true);
    try {
      await deletePronunciationRuleEntry(publisherId, id);
      showSuccess('Rule deleted');
      await loadEntries();
      onChanged?.();
    } catch (e: any) {
      console.error('Delete pronunciation rule failed:', e);
      showError(e?.response?.data?.error || e?.message || 'Failed to delete rule');
    } finally {
      setIsBusy(false);
    }
  };

  const handleSync = async () => {
    setIsBusy(true);
    try {
      await syncPronunciationRules(publisherId);
      showSuccess('Sync started');
      await loadEntries();
      onChanged?.();
    } catch (e: any) {
      console.error('Sync pronunciation rules failed:', e);
      showError(e?.response?.data?.error || e?.message || 'Failed to sync rules');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box>
      <CollapsibleSection title="Quick Speech Test" namespace="pronunciation-rules" id="speech-test">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Convert a short script to speech using a specific ElevenLabs voice. This test automatically uses your first website for context.
        </Typography>
        <Grid container spacing={2}>
          {/* Removed Website selector UI (we auto-select the first website) */}
          <Grid item xs={12} md={8}>
            <TextField
              label="Script text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              fullWidth
              multiline
              minRows={6}
              placeholder="Enter text with optional style tags like [calm], [pause], [emphasized]…"
            />
            <Typography variant="caption" color="text.secondary">
              Tip: Include tags like [calm], [cheerfully], [pause] to shape delivery.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Voice ID"
              value={testVoiceId}
              onChange={(e) => setTestVoiceId(e.target.value)}
              fullWidth
              helperText="ElevenLabs voice identifier"
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
              <Button variant="outlined" onClick={handleUseDefaults} disabled={isTestingSpeech}>Use Defaults</Button>
              <Button variant="contained" onClick={handleTestSpeech} disabled={isTestingSpeech}>
                {isTestingSpeech ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress color="inherit" size={16} />
                    <span>Testing…</span>
                  </Stack>
                ) : 'Test Speech'}
              </Button>
            </Stack>
          </Grid>

          {audioUrl && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Playback</Typography>
                <audio src={audioUrl} controls style={{ width: '100%' }} />
              </Paper>
            </Grid>
          )}
        </Grid>
      </CollapsibleSection>

      {/* Existing toolbar and table below */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2, mt: 2 }}>
        <Typography variant="h6">Pronunciation Rules</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSync} disabled={isBusy || isLoadingList}>Sync</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} disabled={isBusy || isLoadingList}>Add Rule</Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        {isLoadingList ? (
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading rules…</Typography>
          </Box>
        ) : !hasEntries ? (
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            {isBusy && <CircularProgress size={20} />}
            <Typography variant="body2">No pronunciation rules yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="pronunciation rules table">
              <TableHead>
                <TableRow>
                  {/* Removed ID column */}
                  <TableCell>Enabled</TableCell>
                  <TableCell>Phrase</TableCell>
                  <TableCell>Pronunciation</TableCell>
                  <TableCell>Alphabet</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell>Lang</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right" width={140}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(localEntries || []).map((entry, idx) => {
                  const f = getEntryFields(entry);
                  const id = f.id ?? String(idx);
                  return (
                    <TableRow key={id} hover>
                      {/* Removed ID cell */}
                      <TableCell>{f.enabled !== false ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{f.phrase || <em>—</em>}</span>
                          {f.enabled === false ? (
                            <MuiTooltip title="Disabled">
                              <Chip size="small" label="off" />
                            </MuiTooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{f.pronunciation || <em>—</em>}</TableCell>
                      <TableCell>{(entry?.alphabet === 'ipa' || entry?.alphabet === 'arpabet') ? entry.alphabet.toUpperCase() : 'Spelling'}</TableCell>
                      <TableCell>{f.matchType}</TableCell>
                      <TableCell>{f.caseSensitive ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{f.language || '—'}</TableCell>
                      <TableCell>{typeof f.priority === 'number' ? f.priority : '—'}</TableCell>
                      <TableCell>
                        {Array.isArray(f.tags) && f.tags.length ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {f.tags.map((t: string) => (
                              <Chip key={t} size="small" label={t} />
                            ))}
                          </Stack>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size="small" onClick={() => openEditDialog(entry)} disabled={isBusy}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" onClick={() => handleDelete(entry)} disabled={isBusy}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{dialogMode === 'create' ? 'Add Pronunciation Rule' : 'Edit Pronunciation Rule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Phrase to match"
                value={phrase}
                onChange={(e) => { setPhrase(e.target.value); validateRegexIfNeeded(matchType, e.target.value); }}
                fullWidth
                error={!!fieldErrors?.phrase || !!regexError}
                helperText={regexError || fieldErrors?.phrase || 'Text to find in your articles. For regex, use a valid JavaScript pattern.'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pronunciation"
                value={pronunciation}
                onChange={(e) => setPronunciation(e.target.value)}
                fullWidth
                error={!!fieldErrors?.pronunciation}
                helperText={fieldErrors?.pronunciation || 'How it should be read. Example: co-uh-chel-uh'}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
              {/* Pronunciation tips toggle and content */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => setShowPronunciationTips(v => !v)}
                  sx={{ textTransform: 'none' }}
                >
                  Pronunciation tips
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {showPronunciationTips ? 'Hide' : 'Show'}
                </Typography>
              </Stack>
              {showPronunciationTips && (
                <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" gutterBottom>Techniques for Phonetic Spelling</Typography>

                  <Typography variant="body2" fontWeight={600}>Capital Letters for Emphasis</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Use capitals to indicate which syllable should receive stress or emphasis.
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Example: trapezii → trapezIi (emphasizes the "ii" sound)
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>Hyphens for Syllable Breaks</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Insert hyphens to separate syllables and clarify pronunciation breaks.
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Example: Molly Kruit → MOLLee KRU-it
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>Apostrophes</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Use apostrophes to indicate dropped sounds or glottal stops.
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Example: cannot → can't or o'clock
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>Single Quotes Around Letters</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Wrap individual letters in single quotes to force letter-by-letter pronunciation.
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Example: NATO → 'N' 'A' 'T' 'O' (if you want each letter spoken)
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Creating a Pronunciation Rule</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>When adding a rule:</Typography>
                  <ul style={{ marginTop: 0, marginBottom: 8, paddingLeft: 18 }}>
                    <li>
                      <Typography variant="body2"><strong>String to Replace:</strong> Enter the original word exactly as it appears in your text (case-sensitive)</Typography>
                    </li>
                    <li>
                      <Typography variant="body2"><strong>Type:</strong> Select "Alias"</Typography>
                    </li>
                    <li>
                      <Typography variant="body2"><strong>Pronunciation:</strong> Enter your phonetic spelling using the techniques above</Typography>
                    </li>
                  </ul>

                  <Typography variant="body2" fontWeight={600}>Example Rule:</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Original: Molly Kruit
                  </Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                    Pronunciation: MOLLee KRU-it
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Tips for Best Results</Typography>
                  <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 18 }}>
                    <li>
                      <Typography variant="body2"><strong>Experiment:</strong> Try different combinations of capitalization and punctuation to achieve the desired sound</Typography>
                    </li>
                    <li>
                      <Typography variant="body2"><strong>Test:</strong> Always listen to the output and adjust your phonetic spelling as needed</Typography>
                    </li>
                    <li>
                      <Typography variant="body2"><strong>Case Sensitivity:</strong> Remember that rules are case-sensitive—create separate rules for "Word" and "word" if needed</Typography>
                    </li>
                    <li>
                      <Typography variant="body2"><strong>Keep It Simple:</strong> Use the minimum formatting needed to achieve correct pronunciation</Typography>
                    </li>
                  </ul>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="alphabet-label">Pronunciation alphabet</InputLabel>
                <Select
                  labelId="alphabet-label"
                  label="Pronunciation alphabet"
                  value={alphabet}
                  onChange={(e) => setAlphabet(e.target.value as any)}
                >
                  <MenuItem value="spelling">Spelling (phonetic spelling)</MenuItem>
                  <MenuItem value="ipa">IPA (International Phonetic Alphabet)</MenuItem>
                  <MenuItem value="arpabet">ARPAbet (CMU)</MenuItem>
                </Select>
                <FormHelperText>
                  Spelling is easiest. IPA/ARPAbet give precise control over pronunciation.
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} />}
                label="Case sensitive"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                When on, matches must use the same letter casing.
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                label="Enabled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Language (optional)"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="e.g., en, en-US"
                fullWidth
                helperText="Applies this rule only for the specified language. Leave blank for all."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Priority (optional)"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value === '' ? '' : Number(e.target.value))}
                fullWidth
                helperText="Higher priority rules apply before lower ones."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Tags (optional)"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                fullWidth
                placeholder="comma, separated, tags"
                helperText="Labels to organize rules."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Limit to voice IDs (optional)"
                value={appliesToVoices}
                onChange={(e) => setAppliesToVoices(e.target.value)}
                fullWidth
                placeholder="voice_123, voice_456"
                helperText="Only apply for these ElevenLabs voices. Leave blank for all."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="Add context for why this rule exists (not used in TTS)."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                IPA tips: use primary stress ˈ and secondary stress ˌ. ARPAbet tips: separate phonemes with spaces, stress as numbers (e.g., AH0, EH1). Test small changes and listen.
              </Typography>
            </Grid>
            {/* Show Entry ID at the bottom of the form */}
            {editingEntryId && (
              <Grid item xs={12}>
                <TextField
                  label="Entry ID"
                  value={editingEntryId}
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                  helperText="Identifier of this rule (read-only)"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isBusy}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={isBusy}>
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification((n) => ({ ...n, open: false }))}
      />
    </Box>
  );
};

export default PronunciationRulesTab;
